'use client'

import { useState } from 'react'
import { Bell, CheckCheck, Search } from 'lucide-react'

import { NotificationMarkdown } from '@/components/notifications/notification-markdown'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useNotifications } from '@/contexts/notifications-context'
import { useAuth } from '@/contexts/auth-context'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

function formatDate(value?: string | null) {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString('ru-RU')
  } catch {
    return value
  }
}

export function NotificationsBell() {
  const { user } = useAuth()
  const { notifications, unreadCount, markAsRead } = useNotifications()
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null)
  const [zoomImageAlt, setZoomImageAlt] = useState<string>('')

  if (user?.role === 'guest') {
    return null
  }

  const sorted = [...notifications].sort((a, b) => {
    const da = new Date(a.created_at || 0).getTime()
    const db = new Date(b.created_at || 0).getTime()
    return db - da
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-destructive-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[420px] p-0 overflow-hidden">
        <DropdownMenuLabel className="flex items-center justify-between px-4 py-3">
          <span className="text-sm font-semibold">Уведомления</span>
          {unreadCount > 0 ? (
            <Badge variant="secondary" className="text-[11px]">
              {unreadCount} непрочит.
            </Badge>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="m-0" />
        <ScrollArea className="max-h-[520px]">
          {sorted.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">Пока уведомлений нет</div>
          ) : (
            <div className="divide-y divide-border/80">
              {sorted.map((item) => (
                <article
                  key={item.id}
                  className="group/item bg-popover transition-colors hover:bg-muted/35"
                  data-read={item.is_read ? '' : undefined}
                >
                  {item.image_url ? (
                    <button
                      type="button"
                      className="relative block w-full overflow-hidden bg-muted outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => {
                        setZoomImageUrl(item.image_url!)
                        setZoomImageAlt(item.title)
                      }}
                      aria-label="Открыть обложку уведомления крупнее"
                    >
                      {/* Изображение с API / data URL */}
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={item.image_url}
                        alt=""
                        className="aspect-[21/10] max-h-[132px] w-full object-cover"
                      />
                      <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/85 via-transparent to-transparent opacity-0 transition-opacity group-hover/item:opacity-100" />
                      <span className="absolute bottom-2 left-3 inline-flex items-center gap-1 rounded-md bg-black/55 px-2 py-1 text-[11px] font-medium text-white backdrop-blur-sm opacity-90">
                        <Search className="h-3 w-3" aria-hidden />
                        Увеличить
                      </span>
                      {item.is_important ? (
                        <Badge
                          variant="destructive"
                          className="absolute right-3 top-3 text-[10px] shadow-md"
                        >
                          Важное
                        </Badge>
                      ) : (
                        !item.is_read ? (
                          <span className="absolute left-3 top-3 h-2 w-2 rounded-full bg-primary shadow-sm ring-2 ring-background" />
                        ) : null
                      )}
                    </button>
                  ) : null}
                  <div className={`space-y-2 p-3 ${item.image_url ? 'pt-2.5' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 gap-y-1">
                          <h3 className="break-words text-sm font-semibold leading-snug text-foreground">
                            {item.title}
                          </h3>
                          {item.is_important && !item.image_url ? (
                            <Badge variant="destructive" className="shrink-0 text-[10px]">
                              Важное
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {formatDate(item.created_at)}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {item.is_read ? (
                          <Badge variant="outline" className="text-[10px] font-normal">
                            Прочитано
                          </Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Отметить прочитанным"
                            onClick={() => markAsRead(item.id)}
                          >
                            <CheckCheck className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Превью текста: ограничение по высоте + градиент, чтобы список не «расползался» */}
                    <div className="relative -mx-0.5">
                      <div className="max-h-[88px] overflow-hidden rounded-lg border border-border/60 bg-muted/20 px-2.5 py-2">
                        <NotificationMarkdown
                          content={item.body_markdown || ''}
                          className="!text-[12px]"
                        />
                      </div>
                      <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-popover to-transparent"
                        aria-hidden
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
      <Dialog open={Boolean(zoomImageUrl)} onOpenChange={(open) => !open && setZoomImageUrl(null)}>
        <DialogContent className="!h-[96vh] !max-w-[98vw] !w-[98vw] border-none bg-black/95 p-0">
          <DialogTitle className="sr-only">Увеличенное изображение уведомления</DialogTitle>
          <div className="flex h-full w-full items-center justify-center p-2">
            {zoomImageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={zoomImageUrl}
                alt={zoomImageAlt || 'Уведомление'}
                className="max-h-[94vh] max-w-[98vw] object-contain"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </DropdownMenu>
  )
}
